import { useEffect, useRef } from 'react';
import {
  ABSOLUTE_MS,
  ensureSessionStart,
  isAbsoluteExpired,
  type SessionEndReason,
} from './sessionTimeout.ts';

// Fires onExpire once when the admin crosses the absolute session cap while
// enabled.
//
// There is deliberately NO activity tracking here. This hook used to listen for
// mousemove/keydown/click/scroll and sign the admin out after an hour without
// one, which was wrong in three ways at once: it ran per-tab (a background tab
// never saw the activity in the foreground one), it compared wall-clock stamps
// so a sleeping laptop counted as idleness, and it fired against a server
// session that was still valid -- taking the central identity session with it.
// Inactivity is enforced by this portal's own Supabase project now, whose
// 4-hour window every token refresh is measured against; see sessionTimeout.ts.
//
// The 30-second poll stays, and matters for the cap it still enforces: a tab
// that slept through its own 12-hour deadline is caught on the next tick rather
// than never, which a single long setTimeout would not guarantee.
export function useSessionTimeout({
  enabled,
  onExpire,
}: {
  enabled: boolean;
  onExpire: (reason: SessionEndReason) => void;
}): void {
  const fired = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!enabled) return;
    fired.current = false;
    const start = ensureSessionStart(Date.now());
    const check = () => {
      if (fired.current) return;
      if (!isAbsoluteExpired(start, Date.now(), ABSOLUTE_MS)) return;
      fired.current = true;
      onExpireRef.current('expired');
    };
    const interval = window.setInterval(check, 30_000);
    return () => {
      window.clearInterval(interval);
    };
  }, [enabled]);
}
